import * as React from 'react'

import { requestPost } from '../../../../modules/request/index'
import useModalAccessibility from '../../../Shared/hooks/useModalAccessibility'
import { ButtonSpinner, Checkbox, DataTable, pluralize, } from '../../../Shared/index'
import DemoGuardedButton from '../shared/demoGuardedButton'

const smallCheckSrc = `${window.postbuildEnv.CDN_URL}/images/icons/xs/check-small.svg`

const selectClassesHeaders = [
  {
    width: '380px',
    name: 'Class',
    attribute: 'name',
    noTooltip: true
  },
  {
    width: '100px',
    name: 'Students',
    attribute: 'studentCount',
    noTooltip: true
  }
]

const reviewAndMergeHeaders = [
  {
    width: '280px',
    name: 'Class',
    attribute: 'name',
    noTooltip: true
  },
  {
    width: '80px',
    name: 'Students',
    attribute: 'studentCount',
    noTooltip: true
  },
  {
    width: '194px',
    name: 'Role',
    attribute: 'role',
    noTooltip: true
  }
]

interface MergeClassesModalProps {
  classroom: any;
  availableClassrooms: Array<any>;
  close: () => void;
  onSuccess: (snackbarCopy: string) => void;
}

const MergeClassesModal = ({ classroom, availableClassrooms, close, onSuccess, }: MergeClassesModalProps) => {
  const { modalRef, } = useModalAccessibility(close)

  const [step, setStep] = React.useState<number>(1)
  const [selectedClassroomIds, setSelectedClassroomIds] = React.useState<Array<number>>([])
  const [confirmationChecked, setConfirmationChecked] = React.useState<boolean>(false)
  const [processingRequest, setProcessingRequest] = React.useState<boolean>(false)
  const [error, setError] = React.useState<string>(null)

  const selectedClassrooms = availableClassrooms.filter(c => selectedClassroomIds.includes(c.id))
  const secondaryClassText = `${selectedClassroomIds.length} selected secondary ${selectedClassroomIds.length === 1 ? 'class' : 'classes'}`

  function toggleClassroomSelection(id: number) {
    setSelectedClassroomIds(existingIds => {
      return existingIds.includes(id) ? existingIds.filter(existingId => existingId !== id) : existingIds.concat([id])
    })
    setConfirmationChecked(false)
  }

  function toggleConfirmationChecked() {
    setConfirmationChecked(existingValue => !existingValue)
  }

  function handleMergeClassesButtonClick() {
    setProcessingRequest(true)
    setError(null)
    requestPost('/teachers/classroom_merges', {
      primary_classroom_id: classroom.id,
      secondary_classroom_ids: selectedClassroomIds
    }, () => {
      onSuccess('Classes merged')
    }, (body) => {
      setProcessingRequest(false)
      setError(body?.error || 'Classes could not be merged. Please try again.')
    })
  }

  const renderHeader = () => {
    return (
      <div className="merge-classes-modal-header">
        <div className="title-row">
          <h3 className="title" id="mergeClassesModalTitle">Merge Classes</h3>
        </div>
        <ol aria-label="Merge classes steps" className="navigation">
          <li aria-current={step === 1 ? 'step' : undefined} className={step === 1 ? 'active' : ''}>1. Select classes</li>
          <li aria-current={step === 2 ? 'step' : undefined} className={step === 2 ? 'active' : ''}>2. Review and merge</li>
        </ol>
      </div>
    )
  }

  const renderSelectClassesStep = () => {
    const rows = availableClassrooms.map(c => {
      const selected = selectedClassroomIds.includes(c.id)

      return {
        id: c.id,
        checked: selected,
        name: (
          <Checkbox
            id={`select-classroom-${c.id}`}
            label={c.name}
            onClick={() => toggleClassroomSelection(c.id)}
            selected={selected}
          />
        ),
        studentCount: c.students.length
      }
    })

    return (
      <React.Fragment>
        <p className="sub-header">Select one or more classes to merge into <strong>{classroom.name}</strong>.</p>
        <DataTable
          emptyStateMessage="You don&#39;t have any other classes that can be merged into this class."
          headers={selectClassesHeaders}
          reportingFormat={true}
          rows={rows}
        />
        <p className="selected-count">{selectedClassroomIds.length} {pluralize(selectedClassroomIds.length, 'class', 'classes')} selected</p>
        <div className="form-buttons">
          <button className="quill-button outlined green medium focus-on-light" onClick={close} type="button">Cancel</button>
          <button className="quill-button contained green medium focus-on-light" disabled={!selectedClassroomIds.length} onClick={() => setStep(2)} type="button">Next</button>
        </div>
      </React.Fragment>
    )
  }

  const renderReviewAndMergeStep = () => {
    const rows = [{
      id: classroom.id,
      name: classroom.name,
      studentCount: classroom.students.length,
      role: <span className="role-tag"><img alt="" src={smallCheckSrc} />Primary Class</span>
    }].concat(selectedClassrooms.map(c => ({
      id: c.id,
      name: c.name,
      studentCount: c.students.length,
      role: <span className="role-tag">Secondary Class</span>
    })))

    const combinedStudentTotal = new Set([classroom].concat(selectedClassrooms).flatMap(c => c.students.map(s => s.id))).size

    return (
      <React.Fragment>
        <p className="sub-header">Students from the <strong>{secondaryClassText}</strong> will be moved into <strong>{classroom.name}</strong>.</p>
        <DataTable
          headers={reviewAndMergeHeaders}
          reportingFormat={true}
          rows={rows}
        />
        <p className="combined-total">Combined total: <strong>{combinedStudentTotal} {pluralize(combinedStudentTotal, 'student', 'students')}</strong> in <strong>{classroom.name}</strong>.</p>
        <div className="section-divider" />
        <div className="confirmation-checkbox-row">
          <Checkbox
            id="merge-classes-confirmation-checkbox"
            label={`I understand that all students from the ${secondaryClassText} will be transferred to ${classroom.name}.`}
            onClick={toggleConfirmationChecked}
            selected={confirmationChecked}
          />
        </div>
        {error && <p className="error-message">{error}</p>}
        <div className="form-buttons space-between">
          <button className="quill-button outlined green medium focus-on-light" onClick={() => setStep(1)} type="button">Back</button>
          <div className="right-side-buttons">
            <button className="quill-button outlined green medium focus-on-light" onClick={close} type="button">Cancel</button>
            <DemoGuardedButton
              className="quill-button contained green medium focus-on-light"
              disabled={!confirmationChecked || processingRequest}
              onClick={handleMergeClassesButtonClick}
            >
              {processingRequest ? <ButtonSpinner variant="light" /> : 'Merge classes'}
            </DemoGuardedButton>
          </div>
        </div>
      </React.Fragment>
    )
  }

  return (
    <div className="modal-container merge-classes-modal-container">
      <div className="modal-background" />
      <div
        aria-labelledby="mergeClassesModalTitle"
        aria-modal="true"
        className="merge-classes-modal quill-modal modal-body"
        ref={modalRef}
        role="dialog"
        tabIndex={-1}
      >
        {renderHeader()}
        {step === 1 ? renderSelectClassesStep() : renderReviewAndMergeStep()}
      </div>
    </div>
  )
}

export default MergeClassesModal
